import type { CapiPlp } from 'types/capi/plp';
import type { User } from 'types/User';

import gtm from '~/services/gtm.client';
import tiktok from '~/services/tiktok.pixel';
import twitter from '~/services/twitter.pixel';
import {
  getEntrypoint,
  getPreviousPagePath,
  getPreviousPageType,
  getSourceAndMedium
} from '~/utils/navigationContext';

import UserEvent from './UserEvent';

type EventData = {
  clientIp?: string;
  isBot: boolean;
  listId: string;
  plp: CapiPlp;
  user: User;
};

class PlpViewEvent extends UserEvent<EventData> {
  private getProductIds() {
    return this.data.plp.products.map((product) => product.id);
  }

  private getSharedParams() {
    return {
      entry_point: getEntrypoint(),
      list_id: this.data.listId,
      page_path: window.location.pathname,
      page_type: 'plp' as const,
      platform: 'web' as const,
      previous_page_path: getPreviousPagePath(),
      previous_page_type: getPreviousPageType(),
      source_medium: getSourceAndMedium()
    };
  }

  sendToTikTok() {
    return tiktok.fireEvent('ViewContent', {
      content_ids: this.getProductIds(),
      content_type: 'product'
    });
  }

  sendToTwitter() {
    return twitter.fireEvent(twitter.EventIds.ViewContent, {
      contents: this.getProductIds().map((productId) => ({
        content_id: productId,
        content_type: 'product'
      }))
    });
  }

  sendToGtm() {
    return gtm.Ecommerce.viewListItems(
      this.data.plp.products,
      window.location.pathname + window.location.search,
      this.getSharedParams()
    );
  }

  collectServerEvents() {
    const { plp, clientIp } = this.data;
    const url = window.location.href;
    const urlObj = new URL(url);

    const serverEventData = {
      client_ip: clientIp,
      currency: plp.products[0]?.price.currency,
      has_search_term: urlObj.searchParams.has('q'),
      is_filtered: plp.filter_options.some((o) => o.active),
      is_logged_in: this.data.user.isLoggedIn,
      is_sorted: urlObj.searchParams.has('sort'),
      items: this.getListItems(),
      product_type: plp.type,
      useragent: navigator.userAgent,
      ...this.getSharedParams()
    };

    this.serverEvents.push({ data: serverEventData, type: 'plp_view' });
  }

  private getListItems() {
    return this.data.plp.products.map((product, i) => {
      return {
        brand: product.brand,
        item_brand: product.brand,
        item_category: product.genders?.join(', '),
        item_category2: product.sub_type,
        item_id: product.style_code,
        item_name: product.name,
        item_variant: product.model,
        location_id: product.stock_status,
        position: i + 1,
        price: product.price.sale ?? product.price.regular ?? undefined,
        sub_brand: product.sub_brand
      };
    });
  }
}

export default PlpViewEvent;
